import { Column, RowData, Table } from "@tanstack/react-table";

import { PendoId } from "../../../utils/pendo";
import { CellWidth } from "../../Table/Cell";

/**
 * Optional per-column extras carried on `columnDef.meta`, declared by the
 * feature and read by the TST components. TanStack leaves `ColumnMeta` open for
 * consumers, so it comes back through a cast (same pattern as the dashboard
 * table's CustomColumnMeta).
 */
export interface TSTColumnMeta {
    /** A hidden column used only by the filter bar, excluded from Adjust view. */
    filterOnly?: boolean;
    /** Pendo selector for the column's header cell. */
    headerPendoId?: PendoId;
    /** Keeps the column at its declared `size` instead of growing with the table. */
    fixedWidth?: boolean;
}

export function tstColumnMeta<T extends RowData>(
    column: Column<T>
): TSTColumnMeta | undefined {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    return column.columnDef.meta as TSTColumnMeta | undefined;
}

/**
 * Fixed columns get px, the rest a percentage share by size that adds up to
 * 100%. That overflows the table, so `table-layout: fixed` shrinks only the
 * percentage columns and the fixed ones keep their size.
 */
export function tstColumnWidth<T extends RowData>(
    column: Column<T>,
    table: Table<T>
): CellWidth {
    if (tstColumnMeta(column)?.fixedWidth === true) {
        return column.getSize();
    }
    const growableTotal = table
        .getVisibleLeafColumns()
        .filter((visible) => tstColumnMeta(visible)?.fixedWidth !== true)
        .reduce((sum, visible) => sum + visible.getSize(), 0);
    return `${(column.getSize() * 100) / growableTotal}%`;
}
